import { useMemo, useState } from 'react'
import { Award } from 'lucide-react'
import { placesMock } from '../../data/places'
import { useLocale } from '../../i18n/locale'
import { useT } from '../../i18n/t'
import { placeStampById } from '../../data/placeStamps'

import panelJournalBackdropUrl from '../../../zdjecia-panelu/panel-paszport-dolnego-slaska-dziennik.png'

function formatDay(iso: string, localeTag: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(localeTag, { day: '2-digit', month: 'long', year: 'numeric' })
}

function formatMonthYear(iso: string, localeTag: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(localeTag, { month: 'long', year: 'numeric' })
}

type DemoRow = {
  placeId: number
  visitedIso: string
  hasStamp: boolean
  noteKey: 'landing.journalPreview.entry.1.note' | 'landing.journalPreview.entry.2.note' | 'landing.journalPreview.entry.3.note'
  moreKey: 'landing.journalPreview.entry.1.more' | 'landing.journalPreview.entry.2.more' | 'landing.journalPreview.entry.3.more'
}

const DEMO_ROWS: DemoRow[] = [
  {
    placeId: 1,
    visitedIso: '2026-04-12T12:00:00.000Z',
    hasStamp: true,
    noteKey: 'landing.journalPreview.entry.1.note',
    moreKey: 'landing.journalPreview.entry.1.more',
  },
  {
    placeId: 2,
    visitedIso: '2026-07-03T10:30:00.000Z',
    hasStamp: true,
    noteKey: 'landing.journalPreview.entry.2.note',
    moreKey: 'landing.journalPreview.entry.2.more',
  },
  {
    placeId: 3,
    visitedIso: '2026-08-18T15:00:00.000Z',
    hasStamp: true,
    noteKey: 'landing.journalPreview.entry.3.note',
    moreKey: 'landing.journalPreview.entry.3.more',
  },
]

export function LandingJournalPreview() {
  const { localeTag } = useLocale()
  const { t } = useT()
  const [activePlaceId, setActivePlaceId] = useState(DEMO_ROWS[0]!.placeId)
  const [expanded, setExpanded] = useState(false)

  const placeById = useMemo(() => new Map(placesMock.map((p) => [p.id, p])), [])
  const activeRow = DEMO_ROWS.find((r) => r.placeId === activePlaceId) ?? DEMO_ROWS[0]!
  const place = placeById.get(activePlaceId)

  if (!place) return null

  const firstPhoto = place.photos?.[0]
  const coverSrc = typeof firstPhoto === 'string' ? firstPhoto : (firstPhoto?.src ?? '')

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-ink/10 bg-paper shadow-[0_18px_45px_rgba(0,0,0,0.14)]"
      role="region"
      aria-label={t('landing.journalPreview.regionAria')}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: `url(${panelJournalBackdropUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
        aria-hidden
      />
      <div className="relative border-b border-ink/10 bg-paper/90 px-4 py-3 backdrop-blur-[2px] md:px-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/55">
            {t('landing.journalPreview.demoEyebrow')}
          </div>
          <div className="text-[11px] font-semibold text-ink/45">{t('landing.journalPreview.hint')}</div>
        </div>
      </div>

      <div className="relative p-4 md:p-6">
        {/* Mobile: szybki wybór wpisu */}
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1 md:hidden" role="tablist" aria-label={t('landing.journalPreview.tabsAria')}>
          {DEMO_ROWS.map((row) => {
            const p = placeById.get(row.placeId)
            const active = row.placeId === activePlaceId
            return (
              <button
                key={row.placeId}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setActivePlaceId(row.placeId)
                  setExpanded(false)
                }}
                className={[
                  'shrink-0 rounded-lg border px-3 py-2 text-left text-[12px] font-extrabold transition',
                  active ? 'border-ink/25 bg-ink/[0.06] text-ink' : 'border-ink/10 bg-paper text-ink/65 hover:bg-ink/[0.03]',
                ].join(' ')}
              >
                <span className="block truncate max-w-[200px]">{p?.name ?? row.placeId}</span>
                <span className="mt-0.5 block text-[11px] font-semibold text-ink/45">
                  {formatDay(row.visitedIso, localeTag)}
                </span>
              </button>
            )
          })}
        </div>

        <div className="grid gap-4 md:grid-cols-[11rem_1fr] md:gap-6">
          <div className="relative hidden md:block">
            <div className="pointer-events-none absolute left-[13px] top-8 bottom-8 w-px bg-ink/10" aria-hidden />
            <div className="grid gap-6">
              {DEMO_ROWS.map((row, idx) => {
                const p = placeById.get(row.placeId)
                const active = row.placeId === activePlaceId
                const prevIso = idx > 0 ? DEMO_ROWS[idx - 1]!.visitedIso : null
                const showMonth =
                  idx === 0 ||
                  formatMonthYear(row.visitedIso, localeTag) !== (prevIso ? formatMonthYear(prevIso, localeTag) : '')

                return (
                  <div key={row.placeId} className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setActivePlaceId(row.placeId)
                        setExpanded(false)
                      }}
                      className={[
                        'flex w-full items-start gap-3 rounded-lg py-1 text-left transition',
                        active ? '' : 'opacity-75 hover:opacity-100',
                      ].join(' ')}
                    >
                      <div
                        className={[
                          'relative mt-1 grid h-7 w-7 shrink-0 place-items-center pds-tag',
                          active ? 'ring-2 ring-brand/35' : '',
                        ].join(' ')}
                      >
                        <span className={['h-2.5 w-2.5 rounded-full', active ? 'bg-brand' : 'bg-ink/25'].join(' ')} />
                      </div>
                      <div className="min-w-0">
                        {showMonth ? (
                          <div className="text-xs font-extrabold uppercase tracking-wide text-ink/45">
                            {formatMonthYear(row.visitedIso, localeTag)}
                          </div>
                        ) : (
                          <div className="text-xs font-extrabold uppercase tracking-wide text-ink/0 select-none" aria-hidden>
                            ,
                          </div>
                        )}
                        <div className="mt-1 text-sm font-extrabold text-ink">{formatDay(row.visitedIso, localeTag)}</div>
                        <div className="mt-1 line-clamp-2 text-[12px] font-semibold text-ink/55">{p?.name}</div>
                      </div>
                    </button>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="overflow-hidden pds-surface bg-paper shadow-sm">
            <div className="relative">
              <div className="relative h-48 w-full bg-ink/[0.02] sm:h-56 md:h-64">
                {coverSrc ? (
                  <img src={coverSrc} alt={place.name} className="h-full w-full object-cover" loading="lazy" />
                ) : null}
              </div>
              <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-paper via-paper/90 to-transparent" />

              <div className="absolute left-3 top-3 flex flex-wrap gap-2 sm:left-4 sm:top-4">
                <div className="inline-flex items-center gap-2 pds-tag/90 px-3 py-1.5 text-[11px] font-extrabold text-ink sm:px-4 sm:text-xs">
                  {place.category} • {place.region}
                </div>
                {activeRow.hasStamp ? (
                  <div className="pds-tag text-emerald-900 px-3 py-1.5 text-[11px] font-extrabold text-emerald-800 sm:px-4 sm:text-xs">
                    <Award className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                    {t('panel.stamps.badge')}
                  </div>
                ) : null}
              </div>

              {activeRow.placeId === 1 ? (
                <img
                  src={placeStampById[1]!}
                  alt=""
                  aria-hidden
                  className="pointer-events-none absolute -right-2 bottom-10 h-16 w-16 rotate-[-12deg] opacity-90 mix-blend-multiply sm:h-20 sm:w-20"
                  loading="lazy"
                />
              ) : null}
            </div>

            <div className="relative px-4 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
              <div className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">{place.name}</div>

              <p
                className={[
                  'mt-4 whitespace-pre-wrap text-sm font-semibold leading-relaxed text-ink/70 sm:text-base',
                  'first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:text-4xl first-letter:font-extrabold first-letter:leading-[0.9] first-letter:text-ink sm:first-letter:text-5xl',
                ].join(' ')}
              >
                {t(activeRow.noteKey)}
                {expanded ? (
                  <>
                    {' '}
                    {t(activeRow.moreKey)}
                  </>
                ) : null}
              </p>

              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => setExpanded((e) => !e)}
                  className="inline-flex h-10 items-center justify-center pds-tag px-4 text-sm font-extrabold text-ink transition hover:-translate-y-0.5 hover:bg-ink/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 sm:h-11 sm:px-5"
                >
                  {expanded ? t('panel.journal.chapter.collapse') : t('panel.journal.chapter.expand')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
